import { driver } from 'driver.js'
import { memberOrientationSteps } from './memberOrientationSteps'
import { managementOrientationStages } from './managementOrientationSteps'
import { firstPersonOrientationStages } from './firstPersonOrientationSteps'
import { createFirstPersonOrientationUi } from './firstPersonOrientationUi'

const modalSelector = '[aria-modal="true"], dialog[open], .jetstream-modal, #action-dialog, #lens-dialog, #global-search-dialog, [data-overlay-open="true"]'

function isDisplayed(element) {
  if (!element?.isConnected || element.closest('[hidden]')) return false
  for (let node = element; node instanceof HTMLElement; node = node.parentElement) {
    const style = window.getComputedStyle(node)
    if (style.display === 'none' || style.visibility === 'hidden') return false
  }
  return element.getClientRects().length > 0
}

export function isOrientationVisible(element) {
  return isDisplayed(element) && !element.closest('[inert], [aria-hidden="true"]')
}

export function hasOrientationModal() {
  return [...document.querySelectorAll(modalSelector)].some(isDisplayed)
}

function visible(selector, root = document) {
  return [...root.querySelectorAll(selector)].find(isOrientationVisible)
}

function newTabLink(url, text, test, newTabHint) {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  link.dataset.test = test
  link.textContent = text
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  icon.setAttribute('viewBox', '0 0 24 24')
  icon.setAttribute('aria-hidden', 'true')
  icon.setAttribute('class', 'ml-1 inline-block h-4 w-4 fill-current')
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', 'M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z')
  icon.appendChild(path)
  const hint = document.createElement('span')
  hint.className = 'sr-only'
  hint.textContent = ` ${newTabHint}`
  link.append(icon, hint)
  return link
}

export function createGuidedOrientation() {
  let presentation = null
  let controller = null
  let observer = null
  let generation = 0
  let currentOrder = 0
  let activeElement = null
  let context = null
  let moving = false
  let viewportFrame = null

  function synchronizeViewport() {
    if (viewportFrame !== null) return
    const token = generation
    viewportFrame = window.requestAnimationFrame(() => {
      viewportFrame = null
      if (token !== generation || !presentation) return
      revalidate()
      if (token !== generation || !presentation) return
      // Driver only observes window scroll; main's CSS smooth scroll continues
      // after onHighlighted, so nested scroll must refresh the existing stage.
      presentation.refresh()
      positionMenuPopover()
      context?.firstPersonUi?.positionPopover(presentation)
    })
  }

  function setMenu(open) {
    if (context?.management && context.menuContent) {
      context.menuContent.toggleAttribute('data-guided-tour-menu-open', open)
      if (!open) context.menuContent.style.removeProperty('--guided-tour-menu-max-height')
    }
    context?.menu?.dispatchEvent(new CustomEvent('guided-tour-menu', { detail: { open } }))
  }

  function positionMenuPopover() {
    if (!context?.management || !context.definitions[currentOrder]?.menu) return
    const popover = document.querySelector('[data-test="management-orientation.popover"]')
    const menu = context.menuContent
    if (!popover || !menu || !isOrientationVisible(menu)) return
    const mobile = context.menu.dataset.menuPlacement === 'below'
    if (mobile) {
      menu.style.setProperty('--guided-tour-menu-max-height', `${Math.max(80, innerHeight - menu.getBoundingClientRect().top - popover.offsetHeight - 28)}px`)
      activeElement?.scrollIntoView({ block: 'nearest' })
    }
    presentation.refresh()
    const bounds = menu.getBoundingClientRect()
    popover.setAttribute('data-tour-menu-popover', '')
    popover.style.maxWidth = `${innerWidth - (mobile ? 24 : bounds.right + 28)}px`
    popover.style.setProperty('--tour-popover-left', `${mobile ? 12 : bounds.right + 16}px`)
    popover.style.setProperty('--tour-popover-top', `${mobile ? bounds.bottom + 14 : Math.max(12, Math.min(activeElement.getBoundingClientRect().top, innerHeight - popover.offsetHeight - 12))}px`)
  }

  function canRequest(host) {
    return host?.isConnected &&
      !document.querySelector('[data-layout-modal-pending]') &&
      ![...document.querySelectorAll('[data-test="impersonation-banner"], ' + modalSelector)].some(isDisplayed)
  }

  function canOffer(host, path = host?.dataset.memberOrientationPath) {
    return canRequest(host) && path === window.location.pathname
  }

  function eligible() {
    return canOffer(context?.host, context?.path) &&
      (!context?.management || !context.menu || isOrientationVisible(context.menu))
  }

  function resolveDefinitions(definitions) {
    return definitions.map((definition, order) => {
      const root = definition.menu && definition.key !== 'tenant' ? (context.menuContent || document) : document
      const primary = visible(definition.selector, root)
      const element = (definition.list ? context.firstPersonUi?.listElement(primary) : primary) || (definition.actionsMenu && context.firstPersonUi?.menuTrigger()) ||
        (definition.fallback && visible(definition.fallback))
      const key = primary ? definition.key : (definition.fallbackKey || definition.key)
      return { ...definition, order, element, popover: context.labels.steps[key] }
    })
  }

  function plan(steps) {
    const menuAvailable = context.menu && visible(context.menuTrigger, context.menu)
    return steps.filter(step => (!step.unless || !context.menuContent?.querySelector(step.unless)) && (!step.requiresAny || context.menuContent?.querySelector(step.requiresAny)))
      .filter(step => step.element || (step.menu && menuAvailable && (context.menuContent || context.menu).querySelector(step.selector)))
  }

  function resolve(includeHidden = false) {
    const steps = resolveDefinitions(context.definitions)
    if (steps.some(step => step.required && !step.element)) return null
    const planned = plan(steps)
    const progress = context.firstPerson
      ? Object.entries(context.stages).flatMap(([stage, definitions]) => stage === context.stage ? planned : definitions)
      : context.management
        ? Object.entries(managementOrientationStages).flatMap(([stage, definitions]) => {
          if (stage !== 'overview' && !context.menuContent?.querySelector('[data-test="tenant-feature-settings"]')) return []
          const resolved = resolveDefinitions(definitions)
          return plan(resolved).concat(resolved.filter(step => !step.element && step.required && step.key.endsWith('_panel')))
            .sort((first, second) => first.order - second.order)
        })
        : planned
    return planned.filter(step => step.element || includeHidden).map(step => ({
      ...step,
      popover: {
        ...step.popover,
        progressText: context.labels.progress
          .replace('{{current}}', String(progress.findIndex(candidate => candidate.key === step.key) + 1))
          .replace('{{total}}', String(progress.length)),
        ...(context.management && step.menu ? { side: context.menu?.dataset.menuPlacement === 'right' ? 'right' : 'bottom' } : {})
      }
    }))
  }

  function cleanUp(restoreFocus) {
    const hadLifecycle = controller !== null
    controller?.abort()
    observer?.disconnect()
    if (viewportFrame !== null) window.cancelAnimationFrame(viewportFrame)
    viewportFrame = null
    controller = observer = null
    presentation = activeElement = null
    const menu = context?.menu
    const menuWasOpen = context?.menuWasOpen
    setMenu(menuWasOpen === true)
    if (menuWasOpen) {
      // Keep click-away paused until the terminating Driver click has bubbled.
      window.requestAnimationFrame(() => {
        if (context?.menu === menu) return
        menu.dispatchEvent(new CustomEvent('guided-tour-menu', { detail: { open: true } }))
        menu.removeAttribute('data-guided-tour-menu')
      })
    } else menu?.removeAttribute('data-guided-tour-menu')
    moving = false
    context?.firstPersonUi?.destroy()
    context?.actionBar?.dispatchEvent(new CustomEvent('guided-tour-actions', { detail: { open: false } }))
    context = null
    if (hadLifecycle) window.dispatchEvent(new CustomEvent('member-orientation-state', { detail: { active: false } }))
    if (restoreFocus) visible('[data-test="help-trigger"]')?.focus({ preventScroll: true })
  }

  function stop(restoreFocus = true) {
    generation++
    const active = presentation
    if (context) context.restoreFocus = restoreFocus
    active?.destroy()
    // Driver can omit onDestroyed before its first frame. The host always
    // releases its own lifecycle and restores focus after Driver's cleanup.
    cleanUp(restoreFocus)
  }

  function unavailable() {
    const labels = context?.labels
    stop()
    window.dispatchEvent(new CustomEvent('notify', {
      detail: { type: 'info', title: labels?.title, message: labels?.unavailable }
    }))
  }

  async function move(direction) {
    if (moving) return
    if (!eligible()) return stop(false)
    if (direction < 0 && context.multiPage && resolve(true)?.[0]?.order === currentOrder) return
    if (direction > 0 && context.definitions[currentOrder]?.navigate) return navigate(context.definitions[currentOrder].navigate)
    let steps = resolve(context.management)
    if (!steps) return unavailable()
    const next = direction > 0
      ? steps.find(step => step.order > currentOrder)
      : [...steps].reverse().find(step => step.order < currentOrder)
    if (!next) return stop()
    if (context.firstPersonUi) {
      context.firstPersonUi.top()
      if (context.firstPersonUi.prepareList(next) === null) return unavailable()
      if (next.actionsMenu) {
        const token = generation
        moving = true
        if (!context.firstPersonUi.openMenu()) return unavailable()
        const deadline = performance.now() + 1000
        do {
          await new Promise(resolve => window.requestAnimationFrame(resolve))
          if (token !== generation) return
          if (!eligible()) return stop(false)
          if (performance.now() >= deadline) return unavailable()
        } while (!context.firstPersonUi.ensureMenu())
        moving = false
      } else context.firstPersonUi.closeMenu()
      steps = resolve()
      const target = steps?.find(step => step.order === next.order)
      if (!target || !isOrientationVisible(target.element)) return unavailable()
      next.element = target.element
    }
    if (context.menu) {
      const token = generation
      moving = true
      setMenu(next.menu || context.menuWasOpen)
      if (next.menu) {
        const deadline = performance.now() + 1000
        // Wait for Alpine x-show before resolving or highlighting menu entries.
        do {
          await new Promise(resolve => window.requestAnimationFrame(resolve))
          if (token !== generation) return
          if (!eligible()) return stop(false)
          if (performance.now() >= deadline) return unavailable()
        } while (!visible(context.menuReady, context.menuContent || context.menu))
      }
      moving = false
      steps = resolve()
      const target = steps?.find(step => step.order === next.order)
      if (!target || !isOrientationVisible(target.element)) return unavailable()
      next.element = target.element
    }
    currentOrder = next.order
    activeElement = next.element
    context.firstPersonUi?.guardAction(activeElement, next.key)
    presentation.setConfig({ ...presentation.getConfig(), steps })
    presentation.drive(steps.findIndex(step => step.order === next.order))
  }

  function revalidate() {
    if (moving) return
    if (!eligible()) return stop(false)
    if (context.firstPersonUi && !context.firstPersonUi.ensureMenu()) return
    let steps = resolve()
    if (!steps) return unavailable()
    const current = steps.find(step => step.order === currentOrder)
    if (!current) return move(1)
    const listChanged = context.firstPersonUi?.prepareList(current)
    if (listChanged === null) return unavailable()
    if (listChanged) {
      steps = resolve()
      current.element = steps.find(step => step.order === currentOrder).element
    }
    if (current.element !== activeElement) {
      activeElement = current.element
      context.firstPersonUi?.guardAction(activeElement, current.key)
      presentation.setConfig({ ...presentation.getConfig(), steps })
      presentation.drive(steps.findIndex(step => step.order === currentOrder))
    } else if (listChanged) {
      presentation.refresh()
      context.firstPersonUi.positionPopover(presentation)
    }
  }

  async function navigate(step) {
    if (moving) return
    if (!eligible()) return stop(false)
    moving = true
    const token = generation
    const reference = await context.started
    if (token !== generation) return
    if (!eligible()) return stop(false)
    if (!reference) return unavailable()
    const wireId = context.wireId
    const wire = window.Livewire?.find(wireId)
    const removeInterceptor = wire?.$interceptAction?.('continueTour', ({ onError }) => {
      onError(({ preventDefault }) => preventDefault())
    })
    try {
      await wire?.continueTour(reference, step)
      stop(false)
    } catch { unavailable() } finally { removeInterceptor?.() }
  }

  async function request(host, id) {
    if (!canRequest(host)) return false
    const wireId = host.closest('[wire\\:id]')?.getAttribute('wire:id')
    const wire = window.Livewire?.find(wireId)
    const removeInterceptor = wire?.$interceptAction?.('startTour', ({ onError }) => {
      onError(({ preventDefault }) => preventDefault())
    })
    try {
      await wire?.startTour(id)
      return true
    } catch { return false } finally { removeInterceptor?.() }
  }

  async function acknowledge(name, wireId, reference) {
    const wire = window.Livewire?.find(wireId)
    const removeInterceptor = wire?.$interceptAction?.(name, ({ onError }) => {
      onError(({ preventDefault }) => preventDefault())
    })
    try { return await wire?.[name](reference) } finally { removeInterceptor?.() }
  }

  function recordStart() {
    if (!context?.receipt || context.started || !eligible() || !isOrientationVisible(activeElement)) return
    const wireId = context.host.closest('[wire\\:id]')?.getAttribute('wire:id')
    const receipt = context.receipt
    context.wireId = wireId
    const host = context.host
    context.started = acknowledge('confirmTourStarted', wireId, receipt).then(reference => {
      if (reference && host.isConnected) host.dataset.tourState = 'started'
      return reference
    }).catch(() => null)
  }

  function done() {
    const steps = resolve()
    if (moving || !eligible() || !steps || steps.at(-1)?.order !== currentOrder || !isOrientationVisible(activeElement)) return
    const started = context.started
    const wireId = context.wireId
    const host = context.host
    stop()
    // Done can follow the first frame before its acknowledgement returns. Keep
    // the transitions ordered; all other stop paths intentionally omit this.
    started?.then(reference => {
      if (reference) {
        return acknowledge('confirmTourCompleted', wireId, reference).then(() => {
          if (host.isConnected) host.dataset.tourState = 'completed'
        })
      }
    }).catch(() => {})
  }

  async function start(host, { id, module, labels, article = null, receipt = null, stage = null, stages = null, toursUrl = null, path = null }) {
    const management = id === 'management-orientation' && module === id
    const firstPerson = id === 'first-person' && module === id
    if (!management && !firstPerson && (id !== 'member-orientation' || module !== 'member-orientation')) return false
    const stagePlan = firstPerson ? firstPersonOrientationStages(stages) : managementOrientationStages
    const currentStage = stage || (firstPerson ? stages?.[0]?.key : 'overview')
    const definitions = firstPerson || management ? stagePlan?.[currentStage] : memberOrientationSteps
    if (!definitions || ((management || firstPerson) && !path)) return false
    if (controller) return false
    context = { host, labels, article, receipt, restoreFocus: true, management, firstPerson, multiPage: management || firstPerson, stages: stagePlan, stage: currentStage, definitions, path: path || host.dataset.memberOrientationPath, id }
    if (!eligible()) { cleanUp(false); return false }
    if (firstPerson) {
      context.firstPersonUi = createFirstPersonOrientationUi(currentStage, visible, isOrientationVisible)
      context.firstPersonUi.top()
    }
    const token = ++generation
    controller = new AbortController()
    const options = { signal: controller.signal }
    window.addEventListener('help-drawer-show', () => stop(false), options)
    window.addEventListener('keydown', event => {
      if (event.key === 'F1') { event.preventDefault(); stop(false) }
      if (event.key === 'Escape') stop()
    }, { ...options, capture: true })
    document.addEventListener('livewire:navigating', () => stop(false), options)
    window.addEventListener('pagehide', () => stop(false), options)

    // Alpine's trap and leave transitions finish asynchronously. Do not remove
    // inert ourselves: the drawer owns it and must release it normally.
    const deadline = performance.now() + 1000
    while (visible('[data-test="help-drawer"], [data-test="help-drawer.backdrop"]', host) || !visible(management || firstPerson ? definitions[0].selector : '[data-test="home-tiles"]')) {
      if (token !== generation) return false
      if (!eligible() || performance.now() >= deadline) { unavailable(); return false }
      await new Promise(resolve => window.requestAnimationFrame(resolve))
    }
    if (token !== generation) return false
    if (firstPerson) {
      context.actionBar = document.querySelector(`[data-resource="${currentStage}"][data-relation="main"] [data-test="resource-mobile-actions"]`)
      context.actionBar?.dispatchEvent(new CustomEvent('guided-tour-actions', { detail: { open: true } }))
      // The host bar releases aria-hidden through Alpine before choosing anchors.
      await new Promise(resolve => window.requestAnimationFrame(resolve))
      if (token !== generation) return false
      if (!eligible()) { cleanUp(false); return false }
    }
    context.menu = firstPerson ? null : visible(management ? '[data-test="tenant-menu"]' : '[data-test="user-menu"]')
    context.menuTrigger = management ? '[data-test-name="tenant-menu-trigger"]' : '[data-test="user-menu.trigger"]'
    context.menuContent = management ? document.querySelector(`[data-test-name="tenant-menu"][data-menu-placement="${context.menu?.dataset.menuPlacement}"]`) : context.menu
    context.menuReady = management ? '[data-tour-menu-content]' : '[data-test="user-menu.profile"]'
    context.menuWasOpen = !management && !!context.menu && !!visible(context.menuReady, context.menu)
    context.menu?.setAttribute('data-guided-tour-menu', '')
    let steps = resolve()
    if (!steps) { unavailable(); return false }
    if (context.firstPersonUi) {
      if (context.firstPersonUi.prepareList(steps[0]) === null) { unavailable(); return false }
      steps = resolve()
    }
    // The driver captures focus before opening; keep that reference meaningful.
    visible('[data-test="help-trigger"]')?.focus({ preventScroll: true })
    presentation = driver({
      steps,
      // Static transitions also respect reduced-motion preferences.
      animate: false,
      smoothScroll: false,
      disableActiveInteraction: true,
      showProgress: true,
      progressText: labels.progress,
      nextBtnText: labels.next,
      prevBtnText: labels.previous,
      doneBtnText: firstPerson && currentStage !== 'people' ? labels.next : management && (stage === 'features' || (!stage && context.menuContent?.querySelector('[data-test="tenant-feature-settings"]'))) ? labels.next : labels.done,
      closeBtnLabel: labels.close,
      popoverClass: 'member-orientation-popover',
      onNextClick: () => move(1),
      onPrevClick: () => move(-1),
      onDoneClick: () => {
        if ((management || firstPerson) && (context?.definitions[currentOrder]?.navigate || resolve(true)?.at(-1)?.order !== currentOrder)) return move(1)
        done()
      },
      onHighlighted: () => {
        recordStart()
        synchronizeViewport()
      },
      onCloseClick: () => stop(),
      overlayClickBehavior: () => stop(),
      onDestroyed: () => cleanUp(context?.restoreFocus !== false),
      onPopoverRender: popover => {
        popover.wrapper.dataset.test = `${id}.popover`
        popover.wrapper.dataset.tourStep = definitions[currentOrder].key
        popover.nextButton.dataset.test = `${id}.next`
        popover.previousButton.dataset.test = `${id}.previous`
        popover.closeButton.dataset.test = `${id}.close`
        if (management && definitions[currentOrder].key === 'finish' && toursUrl) {
          popover.description.appendChild(newTabLink(
            toursUrl, labels.browse_tours, 'management-orientation.tours', labels.new_tab
          ))
        }
        const currentSteps = resolve()
        if (article?.url && (definitions[currentOrder].article || (!management && !firstPerson && currentSteps?.at(-1).order === currentOrder))) {
          popover.description.appendChild(newTabLink(
            article.url, `${labels.read_more}: ${article.title}`, `${id}.article`, labels.new_tab
          ))
        }
      }
    })
    if ((management || firstPerson) && stage) {
      context.wireId = host.closest('[wire\\:id]')?.getAttribute('wire:id')
      context.started = Promise.resolve(receipt)
    }
    currentOrder = steps[0].order
    activeElement = steps[0].element
    window.dispatchEvent(new CustomEvent('member-orientation-state', { detail: { active: true } }))
    window.addEventListener('resize', revalidate, options)
    window.addEventListener('resize', synchronizeViewport, options)
    document.addEventListener('scroll', synchronizeViewport, { ...options, capture: true, passive: true })
    try {
      presentation.drive(0)
    } catch {
      unavailable()
      return false
    }
    observer = new MutationObserver(revalidate)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'inert', 'aria-hidden', 'style', 'class', 'data-overlay-open'] })
    return true
  }

  return { start, stop, canOffer, canRequest, request }
}
